/**
 * Cleo ERP SaaS — Détail tenant admin (cadrage §3.5)
 *
 * Informations complètes, actions (activer, désactiver, réactiver, supprimer),
 * journal d'audit.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Typography, Tag, Button, Descriptions, Table, Space,
  Spin, Alert, Modal, message, Divider, Timeline,
} from 'antd';
import {
  ArrowLeftOutlined, PlayCircleOutlined, PauseCircleOutlined,
  ReloadOutlined, DeleteOutlined, LinkOutlined,
  LoadingOutlined, ExclamationCircleFilled,
} from '@ant-design/icons';
import api from '../../api/axios';

const { Title, Text } = Typography;

const STATUS_COLORS = {
  EN_ATTENTE: '#F59E0B', ACTIF: '#10B981', 'EXPIRÉ': '#EF4444',
  SUSPENDU: '#64748B', A_SUPPRIMER: '#991B1B', 'SUPPRIMÉ': '#CBD5E1',
};
const STATUS_LABELS = {
  EN_ATTENTE: 'En attente', ACTIF: 'Actif', 'EXPIRÉ': 'Expiré',
  SUSPENDU: 'Suspendu', A_SUPPRIMER: 'À supprimer', 'SUPPRIMÉ': 'Supprimé',
};
const EVENT_LABELS = {
  INSCRIPTION: 'Inscription', VERIFICATION_EMAIL: 'Vérification email',
  ACTIVATION: 'Activation', EXPIRATION_AUTO: 'Expiration automatique',
  DESACTIVATION_MANUELLE: 'Désactivation manuelle', REACTIVATION: 'Réactivation',
  'MARQUÉ_SUPPRESSION': 'Marqué pour suppression', SUPPRESSION: 'Suppression',
  ERREUR_PROVISIONING: 'Erreur provisioning',
};
const COUNTRY_LABELS = {
  MA: 'Maroc', CI: "Côte d'Ivoire", SN: 'Sénégal', ML: 'Mali',
  BF: 'Burkina Faso', TG: 'Togo', BJ: 'Bénin', NE: 'Niger',
  GN: 'Guinée', FR: 'France',
};

function TenantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      const [tenantRes, eventsRes] = await Promise.all([
        api.get(`/api/admin/tenants/${id}/`),
        api.get(`/api/admin/tenants/${id}/events/`),
      ]);
      setTenant(tenantRes.data);
      setEvents(eventsRes.data);
    } catch (err) {
      setError('Tenant introuvable.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [id]);

  const handleAction = async (action, label) => {
    if (action === 'destroy') {
      Modal.confirm({
        title: 'Suppression définitive',
        icon: <ExclamationCircleFilled style={{ color: '#EF4444' }} />,
        content: `Êtes-vous sûr de vouloir supprimer définitivement "${tenant.slug}" ? Cette action est irréversible. Toutes les données seront effacées.`,
        okText: 'Supprimer définitivement',
        okType: 'danger',
        cancelText: 'Annuler',
        onOk: () => executeAction(action, label),
      });
      return;
    }
    await executeAction(action, label);
  };

  const executeAction = async (action, label) => {
    setActionLoading(action);
    try {
      const { data } = await api.post(`/api/admin/tenants/${id}/${action}/`);
      message.success(`${label} effectué avec succès.`);
      if (action === 'destroy') {
        navigate('/admin/tenants');
      } else {
        setTenant(data);
        // Recharger les événements
        const eventsRes = await api.get(`/api/admin/tenants/${id}/events/`);
        setEvents(eventsRes.data);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || `Erreur lors de l'action "${label}".`;
      message.error(detail);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 36, color: '#10B981' }} />} />
      </div>
    );
  }

  if (error) {
    return <Alert message={error} type="error" showIcon style={{ marginTop: 16 }} />;
  }

  if (!tenant) return null;

  const statusConf = { color: STATUS_COLORS[tenant.status], label: STATUS_LABELS[tenant.status] };

  return (
    <div>
      {/* Navigation retour */}
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/admin/tenants')}
        style={{ marginBottom: 16, color: '#64748B' }}
      >
        Retour à la liste
      </Button>

      {/* Card principale */}
      <Card style={{
        borderRadius: 12, border: '1px solid #E5E7EB',
        boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
        marginBottom: 24,
      }}>
        {/* En-tête */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12,
        }}>
          <div>
            <Title level={3} style={{ margin: 0, color: '#0F172A' }}>
              {tenant.slug}
            </Title>
            <Text style={{ color: '#64748B' }}>
              {tenant.company_name} — {COUNTRY_LABELS[tenant.country_code]}
            </Text>
          </div>
          <Tag
            color={statusConf.color}
            style={{ fontSize: 14, padding: '4px 16px', borderRadius: 6 }}
          >
            {statusConf.label}
          </Tag>
        </div>

        {/* Informations détaillées */}
        <Descriptions
          column={{ xs: 1, sm: 2 }}
          size="small"
          labelStyle={{ color: '#64748B', fontWeight: 500, fontSize: 13 }}
          contentStyle={{ color: '#0F172A', fontSize: 13 }}
        >
          <Descriptions.Item label="Port interne">{tenant.internal_port}</Descriptions.Item>
          <Descriptions.Item label="URL instance">
            {tenant.status === 'ACTIF' ? (
              <a href={tenant.instance_url} target="_blank" rel="noopener noreferrer">
                <LinkOutlined style={{ marginRight: 4 }} />
                {tenant.instance_url}
              </a>
            ) : (
              <Text style={{ color: '#94A3B8' }}>{tenant.instance_url}</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Email admin">{tenant.admin_email}</Descriptions.Item>
          <Descriptions.Item label="Email vérifié">
            {tenant.email_verified ? (
              <Tag color="#10B981" style={{ borderRadius: 6 }}>Oui</Tag>
            ) : (
              <Tag color="#F59E0B" style={{ borderRadius: 6 }}>Non</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Mot de passe initial">
            <Text copyable style={{ fontSize: 12, fontFamily: 'monospace' }}>
              {tenant.superuser_password}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Inscrit le">
            {new Date(tenant.created_at).toLocaleString('fr-FR')}
          </Descriptions.Item>
          {tenant.activated_at && (
            <Descriptions.Item label="Activé le">
              {new Date(tenant.activated_at).toLocaleString('fr-FR')}
              {tenant.activated_by_email && (
                <Text style={{ color: '#64748B', marginLeft: 4 }}>
                  par {tenant.activated_by_email}
                </Text>
              )}
            </Descriptions.Item>
          )}
          {tenant.trial_end_date && (
            <Descriptions.Item label="Expire le">
              <Text style={{
                color: tenant.days_remaining === 0 ? '#EF4444' : '#0F172A',
              }}>
                {new Date(tenant.trial_end_date).toLocaleString('fr-FR')}
                {tenant.days_remaining > 0 && ` (J-${tenant.days_remaining})`}
              </Text>
            </Descriptions.Item>
          )}
          {tenant.deactivated_at && (
            <Descriptions.Item label="Désactivé le">
              {new Date(tenant.deactivated_at).toLocaleString('fr-FR')}
            </Descriptions.Item>
          )}
        </Descriptions>

        <Divider style={{ margin: '20px 0' }} />

        {/* Actions */}
        <Space wrap>
          {(tenant.status === 'EN_ATTENTE' || tenant.status === 'EXPIRÉ') && (
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              loading={actionLoading === 'activate'}
              onClick={() => handleAction('activate', 'Activation')}
            >
              Activer
            </Button>
          )}
          {tenant.status === 'ACTIF' && (
            <Button
              icon={<PauseCircleOutlined />}
              loading={actionLoading === 'deactivate'}
              onClick={() => handleAction('deactivate', 'Désactivation')}
            >
              Désactiver
            </Button>
          )}
          {['EXPIRÉ', 'SUSPENDU', 'A_SUPPRIMER'].includes(tenant.status) && (
            <Button
              icon={<ReloadOutlined />}
              loading={actionLoading === 'reactivate'}
              onClick={() => handleAction('reactivate', 'Réactivation')}
              style={{ color: '#10B981', borderColor: '#10B981' }}
            >
              Réactiver
            </Button>
          )}
          {tenant.status !== 'ACTIF' && tenant.status !== 'SUPPRIMÉ' && (
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={actionLoading === 'destroy'}
              onClick={() => handleAction('destroy', 'Suppression')}
            >
              Supprimer définitivement
            </Button>
          )}
        </Space>
      </Card>

      {/* Journal d'audit */}
      <Card
        title={
          <Title level={5} style={{ margin: 0, color: '#0F172A' }}>
            Journal d'audit
          </Title>
        }
        style={{ borderRadius: 12, border: '1px solid #E5E7EB' }}
      >
        {events.length === 0 ? (
          <Text style={{ color: '#94A3B8' }}>Aucun événement.</Text>
        ) : (
          <Timeline
            items={events.map((event) => ({
              color: event.event_type === 'ACTIVATION' ? '#10B981'
                : event.event_type === 'ERREUR_PROVISIONING' ? '#EF4444'
                : event.event_type === 'EXPIRATION_AUTO' ? '#F59E0B'
                : '#E5E7EB',
              children: (
                <div>
                  <Text strong style={{ color: '#0F172A', fontSize: 13 }}>
                    {EVENT_LABELS[event.event_type] || event.event_type}
                  </Text>
                  <Text style={{ color: '#64748B', fontSize: 12, marginLeft: 8 }}>
                    {new Date(event.created_at).toLocaleString('fr-FR')}
                    {event.actor && ` — ${event.actor}`}
                  </Text>
                  {event.details && (
                    <div>
                      <Text style={{ color: '#94A3B8', fontSize: 12 }}>
                        {event.details}
                      </Text>
                    </div>
                  )}
                </div>
              ),
            }))}
          />
        )}
      </Card>
    </div>
  );
}

export default TenantDetailPage;
